// /consent/NoticeOfPrivacyPracticesModal.js
import React from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    Box,
    Divider,
} from "@mui/material";

export default function NoticeOfPrivacyPracticesModal({ open, onClose }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Notice of Privacy Practices</DialogTitle>

            <DialogContent dividers>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Tapestry TeleHealth, PLLC; Chess Medicine, PLLC; Chess Medical, PC
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Notice of Privacy Practices
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    This notice describes how medical information about you may be used and disclosed and how you can get access
                    to this information. Please review it carefully.
                </Typography>

                <Typography variant="subtitle1" sx={{ mt: 3, fontWeight: 800 }}>
                    Your Information. Your Rights. Our Responsibilities.
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 800 }}>
                    When it comes to your health information, you have certain rights.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    This section explains your rights and some of our responsibilities to help you.
                </Typography>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Get an electronic or paper copy of your medical record
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        You can ask to see or get an electronic or paper copy of your medical record and other health information we
                        have about you. Ask us how to do this. We will provide a copy or a summary of your health information,
                        usually within 30 days of your request. We may charge a reasonable, cost-based fee.
                    </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Ask us to correct your medical record
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        You can ask us to correct health information about you that you think is incorrect or incomplete. Ask us
                        how to do this. We may say “no” to your request, but we’ll tell you why in writing within 60 days.
                    </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Request confidential communications
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        You can ask us to contact you in a specific way (for example, home or office phone) or to send mail to a
                        different address. We will say “yes” to all reasonable requests.
                    </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Ask us to limit what we use or share
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        You can ask us not to use or share certain health information for treatment, payment, or our operations. We
                        are not required to agree to your request, and we may say “no” if it would affect your care. If you pay for
                        a service or health care item out-of-pocket in full, you can ask us not to share that information for the
                        purpose of payment or our operations with your health insurer. We will say “yes” unless a law requires us
                        to share that information.
                    </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Get a list of those with whom we’ve shared information
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        You can ask for a list (accounting) of the times we’ve shared your health information for six years prior to
                        the date you ask, who we shared it with, and why. We will include all the disclosures except for those
                        about treatment, payment, and health care operations, and certain other disclosures (such as any you asked
                        us to make). We’ll provide one accounting a year for free but will charge a reasonable, cost-based fee if
                        you ask for another one within 12 months.
                    </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Get a copy of this privacy notice
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        You can ask for a paper copy of this notice at any time, even if you have agreed to receive the notice
                        electronically. We will provide you with a paper copy promptly.
                    </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Choose someone to act for you
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        If you have given someone medical power of attorney or if someone is your legal guardian, that person can
                        exercise your rights and make choices about your health information. We will make sure the person has this
                        authority and can act for you before we take any action.
                    </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        File a complaint if you feel your rights are violated
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        You can complain if you feel we have violated your rights by contacting us using the information on the
                        back page. You can file a complaint with the U.S. Department of Health and Human Services Office for Civil
                        Rights by sending a letter to 200 Independence Avenue, S.W., Washington, D.C. 20201, calling 1-877-696-6775,
                        or visiting{" "}
                        <a
                            href="https://www.hhs.gov/ocr/privacy/hipaa/complaints/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            www.hhs.gov/ocr/privacy/hipaa/complaints/
                        </a>
                        . We will not retaliate against you for filing a complaint.
                    </Typography>
                </Box>

                <Typography variant="subtitle2" sx={{ mt: 3, fontWeight: 800 }}>
                    For certain health information, you can tell us your choices about what we share.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    If you have a clear preference for how we share your information in the situations described below, talk to
                    us. Tell us what you want us to do, and we will follow your instructions.
                </Typography>

                <Box component="ul" sx={{ mt: 1, mb: 2, pl: 3, lineHeight: 1.7 }}>
                    <li>Share information with your family, close friends, or others involved in your care</li>
                    <li>Share information in a disaster relief situation</li>
                    <li>Include your information in a directory</li>
                </Box>

                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    If you are not able to tell us your preference, for example if you are unconscious, we may go ahead and share
                    your information if we believe it is in your best interest. We may also share your information when needed to
                    lessen a serious and imminent threat to health or safety.
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 3, fontWeight: 800 }}>
                    In these cases we never share your information unless you give us written permission:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 2, pl: 3, lineHeight: 1.7 }}>
                    <li>Marketing purposes</li>
                    <li>Sale of your information</li>
                    <li>Most sharing of psychotherapy notes</li>
                </Box>

                <Typography variant="subtitle2" sx={{ mt: 3, fontWeight: 800 }}>
                    How do we typically use or share your health information?
                </Typography>

                <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Treat you
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        We can use your health information and share it with other professionals who are treating you. Example: A
                        doctor treating you for an injury asks another doctor about your overall health condition.
                    </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Run our organization
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        We can use and share your health information to run our practice, improve your care, and contact you when
                        necessary. Example: We use health information about you to manage your treatment and services.
                    </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Bill for your services
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        We can use and share your health information to bill and get payment from health plans or other entities.
                        Example: We give information about you to your health insurance plan so it will pay for your services.
                    </Typography>
                </Box>

                <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                    continued on next page
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 3, fontWeight: 800 }}>
                    How else can we use or share your health information?
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    We are allowed or required to share your information in other ways, usually in ways that contribute to the
                    public good, such as public health and research. We have to meet many conditions in the law before we can
                    share your information for these purposes. For more information see:{" "}
                    <a
                        href="https://www.hhs.gov/ocr/privacy/hipaa/understanding/consumers/index.html"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        www.hhs.gov/ocr/privacy/hipaa/understanding/consumers/index.html
                    </a>
                    .
                </Typography>

                <Box component="ul" sx={{ mt: 1, mb: 2, pl: 3, lineHeight: 1.7 }}>
                    <li>Preventing disease</li>
                    <li>Helping with product recalls</li>
                    <li>Reporting adverse reactions to medications</li>
                    <li>Reporting suspected abuse, neglect, or domestic violence</li>
                    <li>Preventing or reducing a serious threat to anyone’s health or safety</li>
                </Box>

                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 800 }}>
                    Do research
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    We can use or share your information for health research.
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 800 }}>
                    Comply with the law
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    We will share information about you if state or federal laws require it, including with the Department of
                    Health and Human Services if it wants to see that we’re complying with federal privacy law.
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 800 }}>
                    Respond to organ and tissue donation requests
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    We can share health information about you with organ procurement organizations.
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 800 }}>
                    Work with a medical examiner or funeral director
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    We can share health information with a coroner, medical examiner, or funeral director when an individual
                    dies.
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 800 }}>
                    Address workers’ compensation, law enforcement, and other government requests
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    We can use or share health information about you:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 2, pl: 3, lineHeight: 1.7 }}>
                    <li>For workers’ compensation claims</li>
                    <li>For law enforcement purposes or with a law enforcement official</li>
                    <li>With health oversight agencies for activities authorized by law</li>
                    <li>For special government functions such as military, national security, and presidential protective services</li>
                </Box>

                <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 800 }}>
                    Respond to lawsuits and legal actions
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    We can share health information about you in response to a court or administrative order, or in response to a
                    subpoena.
                </Typography>

                <Typography variant="body2" sx={{ mt: 3, lineHeight: 1.7 }}>
                    Certain health information may be subject to more stringent protections, such as under Part 2 (substance
                    abuse regulations) or under applicable state law. Please note that (i) Tapestry TeleHealth, PLLC, (ii) Chess
                    Medicine, PLLC, and (iii) Chess Medical, PC are third party health care providers. We do not create or
                    maintain a facility directory. You may receive a separate HIPAA Notice of Privacy Practices from a facility,
                    such as a nursing home, rehabilitation facility, or nursing facility. Those facilities will have their own
                    HIPAA Notice of Privacy Practices, which govern their own use and disclosure of patients’ health information.
                    Sometimes we provide services as a vendor to other organizations. Other times, we provide services as a
                    health care provider, including but not limited to patient care services, monitoring, and chronic care.
                    Individuals might receive some or all of these services as appropriate.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    We are required by law to maintain the privacy and security of your protected health information. We will let
                    you know promptly if a breach occurs that may have compromised the privacy or security of your information.
                    We must follow the duties and privacy practices described in this notice and give you a copy of it. We will
                    not use or share your information other than as described here unless you tell us we can in writing. If you
                    tell us we can, you may change your mind at any time. Let us know in writing if you change your mind.
                </Typography>

                <Typography variant="body2" sx={{ mt: 2 }}>
                    For more information see:{" "}
                    <a
                        href="https://www.hhs.gov/ocr/privacy/hipaa/understanding/consumers/noticepp.html"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        www.hhs.gov/ocr/privacy/hipaa/understanding/consumers/noticepp.html
                    </a>
                    .
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 3, fontWeight: 800 }}>
                    Changes to the Terms of This Notice
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    We can change the terms of this notice, and the changes will apply to all information we have about you. The
                    new notice will be available upon request, in our office, and on our web site.
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 3, fontWeight: 800 }}>
                    This Notice of Privacy Practices applies to the following organizations.
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    For purposes of HIPAA, Tapestry TeleHealth, PLLC, Chess Medicine, PLLC, and Chess Medical, PC are an
                    Affiliated Covered Entity (ACE) and are also an Organized Health Care Arrangement (OHCA) for certain joint
                    activities, such as treatment, payment and health care operations. These entities have agreed to a joint
                    HIPAA Notice of Privacy Practices.
                </Typography>

                <Typography variant="body2" sx={{ mt: 3, lineHeight: 1.7 }}>
                    To exercise your HIPAA rights or make a complaint, please contact Tapestry Health’s HIPAA Privacy Officer at{" "}
                    <a href="mailto:compliance@tapestryhealth.com">compliance@tapestryhealth.com</a>, by phone at 203-666-8145, or
                    HIPAA Privacy Officer, 99 Hawley Lane, Stratford, CT 06614.
                </Typography>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
